import { env } from './env';

interface ApiOptions extends RequestInit {
  json?: unknown;
  /** Skip attaching the Supabase JWT (for public endpoints). */
  skipAuth?: boolean;
  /** Abort the request after N ms (default 20s). 0 disables the timeout. */
  timeoutMs?: number;
  /** Internal: this is the retry after renewing a rejected session. */
  isSessionRetry?: boolean;
}

const DEFAULT_TIMEOUT_MS = 20_000;

/**
 * For requests that move whole images server-side (confirm upload, replace
 * image, commit variants): each copies the original plus its variants between
 * R2 keys, which can outlast the default timeout on large panoramas.
 */
export const IMAGE_COMMIT_TIMEOUT_MS = 120_000;

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    public readonly body: unknown,
    message: string,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

/**
 * The API's answers to a token it no longer accepts. Other 401s (a wrong tour
 * password, a bad webhook signature) are about the request, not the session.
 */
const SESSION_REJECTED_MESSAGES = new Set(['Invalid or expired token', 'Authentication required']);

/** Shared by every request that hits a rejected token at the same time. */
let sessionRenewal: Promise<boolean> | null = null;

/**
 * The app still holds a session the API refuses (revoked elsewhere, password
 * changed on another device). Try one refresh; if that fails, sign out — the
 * protected routes then send the user to /login and back here afterwards.
 */
function renewRejectedSession(): Promise<boolean> {
  sessionRenewal ??= (async () => {
    const { supabase } = await import('./supabase-client');
    const { data, error } = await supabase.auth.refreshSession();
    if (!error && data.session) return true;
    await supabase.auth.signOut();
    return false;
  })().finally(() => {
    sessionRenewal = null;
  });
  return sessionRenewal;
}

/** The API (or a lookup by slug) said the thing does not exist. */
export function isNotFoundError(err: unknown): boolean {
  return err instanceof ApiError && err.status === 404;
}

/**
 * The panorama isn't there for this user. The API answers 403 for a deleted
 * one too (`requirePanoramaPermission`): an id outside the user's organizations
 * must not reveal whether it exists.
 */
export function isPanoramaUnavailableError(err: unknown): boolean {
  return err instanceof ApiError && (err.status === 404 || err.status === 403);
}

export async function apiFetch<T = unknown>(
  path: string,
  opts: ApiOptions = {},
): Promise<T> {
  const headers = new Headers(opts.headers ?? {});
  if (opts.json !== undefined) {
    headers.set('Content-Type', 'application/json');
  }
  let sentToken = false;
  if (!opts.skipAuth) {
    // Lazy: `supabase-client` builds the client (and throws without
    // VITE_SUPABASE_URL) at import time. Importing it here would drag that into
    // every module that merely imports `api`, e.g. under vitest in CI.
    const { supabase } = await import('./supabase-client');
    const { data } = await supabase.auth.getSession();
    const token = data.session?.access_token;
    if (token) {
      headers.set('Authorization', `Bearer ${token}`);
      sentToken = true;
    }
  }

  // Default 20s timeout so a hung backend can't keep the UI in "loading"
  // forever. If the caller already passed a signal, chain it so both can
  // abort the request.
  const timeoutMs = opts.timeoutMs ?? DEFAULT_TIMEOUT_MS;
  const timeoutCtrl = timeoutMs > 0 ? new AbortController() : null;
  const timer = timeoutCtrl
    ? setTimeout(() => timeoutCtrl.abort(new DOMException('Request timeout', 'TimeoutError')), timeoutMs)
    : null;
  const signal = timeoutCtrl ? timeoutCtrl.signal : opts.signal;

  let res: Response;
  try {
    res = await fetch(`${env.API_BASE_URL}/api/v1${path}`, {
      ...opts,
      headers,
      body: opts.json !== undefined ? JSON.stringify(opts.json) : opts.body,
      credentials: 'include',
      signal,
    });
  } catch (err) {
    if (timer) clearTimeout(timer);
    if (err instanceof DOMException && err.name === 'TimeoutError') {
      throw new ApiError(0, null, 'Request timed out — backend not responding');
    }
    if (err instanceof DOMException && err.name === 'AbortError') {
      throw new ApiError(0, null, 'Request aborted');
    }
    // Network error (CORS, DNS, server down). fetch throws TypeError here.
    throw new ApiError(0, null, err instanceof Error ? err.message : 'Network error');
  }
  if (timer) clearTimeout(timer);

  if (!res.ok) {
    let body: unknown = null;
    try {
      body = await res.json();
    } catch {
      // ignore
    }
    let message = `HTTP ${res.status}`;
    if (body && typeof body === 'object' && 'message' in body) {
      const msg = (body).message;
      if (typeof msg === 'string' && msg.length > 0) message = msg;
    }
    if (res.status === 401 && sentToken && SESSION_REJECTED_MESSAGES.has(message)) {
      if (!opts.isSessionRetry && (await renewRejectedSession())) {
        return apiFetch<T>(path, { ...opts, isSessionRetry: true });
      }
      throw new ApiError(401, body, 'Session expired');
    }
    throw new ApiError(res.status, body, message);
  }

  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
}

export const api = {
  get: <T>(path: string, opts?: Omit<ApiOptions, 'method' | 'body'>) =>
    apiFetch<T>(path, { ...opts, method: 'GET' }),
  post: <T>(path: string, json?: unknown, opts?: Omit<ApiOptions, 'method' | 'body' | 'json'>) =>
    apiFetch<T>(path, { ...opts, method: 'POST', json }),
  put: <T>(path: string, json?: unknown, opts?: Omit<ApiOptions, 'method' | 'body' | 'json'>) =>
    apiFetch<T>(path, { ...opts, method: 'PUT', json }),
  patch: <T>(path: string, json?: unknown, opts?: Omit<ApiOptions, 'method' | 'body' | 'json'>) =>
    apiFetch<T>(path, { ...opts, method: 'PATCH', json }),
  delete: <T>(path: string, opts?: Omit<ApiOptions, 'method' | 'body'>) =>
    apiFetch<T>(path, { ...opts, method: 'DELETE' }),
};
